import React, { useEffect, useMemo, useState } from 'react';
import { Button, ButtonToolbar, Spinner } from 'react-bootstrap';
import { useTranslationWithRef } from '../../hooks/useTranslationWithRef';
import { getAllConsents, saveConsents } from '../../api/ShoppingCart';
import Consent from '../../api/models/Consent';
import { APIError } from '../../api/ObservableFromFetch';
import { toast } from 'react-toastify';
import ConsentForm from '../Results/Modals/ConsentForm';
import OrderItem from '../../api/models/addOnTesting/ShoppingCart';

interface ConsentProps {
  cartItems: OrderItem[]
  testEventId: number
  onBack: () => void
  onSubmit: () => void
  invalidate: () => void
}

export const ConsentStep = (props: ConsentProps) => {
  const [translationRef, t] = useTranslationWithRef()
  const [loading, setLoading] = useState(true)
  const [consents, setConsents] = useState<Consent[]>([])
  const [acceptedConsents, setAcceptedConsents] = useState<number[]>([])
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    const subscription = getAllConsents(props.testEventId).subscribe({
      next: (results: Consent[]) => {
        setConsents(results)
        setLoading(false)
      },
      error: (e: APIError) => {
        setLoading(false)
        toast.error(translationRef.current(e.message, { data: e.data }))
      }
    })
    return () => subscription.unsubscribe()
  }, [translationRef, props.testEventId])

  const saveConsent = (values: number[]) => {
    if (saving) {
      return
    }
    setSaving(true)
    saveConsents({id: props.testEventId, ids: values}).subscribe({
      next: (updatedItems: OrderItem[]) => {
        setSaving(false)
        if (updatedItems.length !== props.cartItems.length) {
          toast.error(t("shoppingCart.cartError"))
          props.invalidate()
          props.onBack()
          return
        }
        props.onSubmit()
      },
      error: (e: APIError) => {
        toast.error(t(e.message, { data: e.data }))
        setSaving(false)
      }
    })
  }

  const sortedCartItems = useMemo(() => props.cartItems.sort((itemA, itemB) => itemA.name.localeCompare(itemB.name)), [props.cartItems])

  return (
  <>
    <h4 className="text-center">{sortedCartItems.map(item => item.name).toString()}</h4>
    {loading && <Spinner animation="border" role="status" size="sm" className="mx-1" />}
    {!loading && consents.length > 0 && consents.map(consent => <ConsentForm key={consent.consentId} consent={consent} implementation={undefined}
      initialAccepted={false} onCancel={() => { }} onSubmit={() => { }}
      onChange={(accepted) => setAcceptedConsents(accepted ? [...acceptedConsents, consent.consentId] : acceptedConsents.filter(consentId => consentId !== consent.consentId))}
    />)}
    <ButtonToolbar className="justify-content-between">
      <Button variant="secondary" onClick={() => props.onBack()}>{t('shoppingCart.back')}</Button>
      <Button disabled={saving || consents.length === 0 || acceptedConsents.length !== consents.length} onClick={() => saveConsent(acceptedConsents)}>{t('shoppingCart.nextStep')}</Button>
    </ButtonToolbar>
  </>
  )
}
